import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import { adminApi } from '../lib/adminAuth';
import {
  Download, Upload, Database, History, Shield, FileArchive,
  FileSpreadsheet, FileText, CheckCircle2, AlertTriangle, XCircle,
  Plus, Pencil, Trash2, Minus, ChevronDown, ChevronRight, Loader2,
  AlertOctagon, RotateCcw,
} from 'lucide-react';

/*
  Data Export / Import — super-admin surgical read/write for the whole DB.
  Four tabs:  Export · Import · Backups · Audit
  Hard-wired against POST /api/admin/data-io/*  (see routes/data_io.py)
*/
export default function DataIOPage() {
  const [tab, setTab] = useState('export');
  return (
    <div className="p-6 space-y-6" data-testid="data-io-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-[#1a1a2e] flex items-center gap-2">
            <Database className="w-6 h-6" />Data Export / Import
          </h1>
          <p className="text-sm text-neutral-500 mt-1">
            Super-admin tool. Export selected collections → edit externally (Claude / Excel) → re-upload with a
            full diff preview and automatic rollback-ready backup.
          </p>
        </div>
      </div>
      <div className="flex gap-1 border-b border-neutral-200">
        {[
          { k: 'export',  label: 'Export',   icon: Download },
          { k: 'import',  label: 'Import',   icon: Upload },
          { k: 'backups', label: 'Backups',  icon: FileArchive },
          { k: 'audit',   label: 'Audit',    icon: History },
        ].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)}
            data-testid={`data-io-tab-${t.k}`}
            className={`px-4 py-2 flex items-center gap-2 text-sm font-medium border-b-2 -mb-px transition
              ${tab === t.k ? 'border-fuchsia-600 text-fuchsia-700' : 'border-transparent text-neutral-500 hover:text-neutral-800'}`}>
            <t.icon className="w-4 h-4" />{t.label}
          </button>
        ))}
      </div>
      {tab === 'export'  && <ExportTab />}
      {tab === 'import'  && <ImportTab />}
      {tab === 'backups' && <BackupsTab />}
      {tab === 'audit'   && <AuditTab />}
    </div>
  );
}

// ─────────────────────────── EXPORT ───────────────────────────
function ExportTab() {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [format, setFormat] = useState('zip-json');
  const [includePii, setIncludePii] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await adminApi.get('/api/admin/data-io/collections');
        setCollections(r.data.collections || []);
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Load failed');
      } finally { setLoading(false); }
    })();
  }, []);

  const groups = useMemo(() => {
    const g = {};
    collections.forEach(c => { (g[c.group] = g[c.group] || []).push(c); });
    return g;
  }, [collections]);

  const toggleCol = (n) => setSelected(prev => {
    const s = new Set(prev); if (s.has(n)) s.delete(n); else s.add(n); return s;
  });
  const toggleGroup = (group, all) => setSelected(prev => {
    const s = new Set(prev);
    (groups[group] || []).forEach(c => { if (all) s.add(c.name); else s.delete(c.name); });
    return s;
  });

  const selectAll = () => setSelected(new Set(collections.map(c => c.name)));
  const clearAll  = () => setSelected(new Set());

  const estSize = useMemo(() => {
    let kb = 0;
    collections.forEach(c => { if (selected.has(c.name)) kb += c.size_kb; });
    return kb;
  }, [selected, collections]);

  const anyPiiSelected = useMemo(() => collections.some(c => selected.has(c.name) && c.pii), [collections, selected]);

  const download = async () => {
    if (!selected.size) return toast.error('Pick at least one collection');
    if (anyPiiSelected && includePii && !window.confirm(
      '⚠️ Selected collections contain PII (emails, phones, PAN, salary, etc.). Include real values in the export?\n\nUncheck "Include PII" if you want hashed values instead.')) {
      return;
    }
    setDownloading(true);
    try {
      const r = await adminApi.post('/api/admin/data-io/export',
        { collections: Array.from(selected), format, include_pii: includePii },
        { responseType: 'blob' });
      const blob = r.data;
      const dispo = r.headers['content-disposition'] || '';
      const match = dispo.match(/filename="([^"]+)"/);
      const fname = match ? match[1] : `girgit_export_${Date.now()}`;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = fname; a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${fname}`);
    } catch (e) {
      toast.error('Export failed');
    } finally { setDownloading(false); }
  };

  if (loading) return <div className="p-8 text-center text-neutral-400"><Loader2 className="w-6 h-6 animate-spin inline" /></div>;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-5">
      {/* Checkbox tree */}
      <div className="bg-white rounded-xl border border-neutral-200">
        <div className="flex items-center justify-between px-5 py-3 border-b border-neutral-200">
          <div className="font-semibold text-[#1a1a2e]">Collections</div>
          <div className="flex gap-2">
            <button onClick={selectAll} className="text-xs text-fuchsia-700 hover:underline" data-testid="data-io-select-all">Select all</button>
            <button onClick={clearAll} className="text-xs text-neutral-500 hover:underline" data-testid="data-io-clear">Clear</button>
          </div>
        </div>
        <div className="divide-y divide-neutral-100 max-h-[70vh] overflow-y-auto">
          {Object.entries(groups).map(([group, cols]) => {
            const groupSelected = cols.filter(c => selected.has(c.name)).length;
            const allOn = groupSelected === cols.length;
            return (
              <div key={group} data-testid={`data-io-group-${group}`}>
                <div className="flex items-center gap-2 px-5 py-2 bg-neutral-50 sticky top-0 z-10">
                  <input type="checkbox" checked={allOn}
                    onChange={() => toggleGroup(group, !allOn)}
                    className="w-4 h-4 accent-fuchsia-600" />
                  <span className="font-semibold text-sm text-neutral-700 flex-1">{group}</span>
                  <span className="text-xs text-neutral-400">{groupSelected}/{cols.length}</span>
                </div>
                {cols.map(c => (
                  <label key={c.name}
                    className={`flex items-center gap-3 px-5 py-2 hover:bg-neutral-50 cursor-pointer ${!c.editable ? 'opacity-80' : ''}`}
                    data-testid={`data-io-row-${c.name}`}>
                    <input type="checkbox" checked={selected.has(c.name)}
                      onChange={() => toggleCol(c.name)}
                      className="w-4 h-4 accent-fuchsia-600" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-neutral-800 flex items-center gap-2">
                        {c.name}
                        {!c.editable && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-neutral-100 text-neutral-500 rounded">read-only</span>
                        )}
                        {c.pii && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-rose-50 text-rose-600 border border-rose-200 rounded flex items-center gap-1">
                            <Shield className="w-3 h-3" />PII
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-xs text-neutral-500 tabular-nums whitespace-nowrap">{c.count} rows · {c.size_kb} KB</div>
                  </label>
                ))}
              </div>
            );
          })}
        </div>
      </div>

      {/* Right panel: format + action */}
      <div className="bg-white rounded-xl border border-neutral-200 p-5 h-fit">
        <div className="font-semibold text-[#1a1a2e] mb-3">Export options</div>
        <div className="space-y-2">
          {[
            { k: 'zip-json',   label: 'ZIP of JSON', icon: FileArchive, note: 'Best for Claude — lossless, nested data preserved' },
            { k: 'xlsx',       label: 'Excel (XLSX)', icon: FileSpreadsheet, note: 'Readable in Excel; nested fields flatten with dot-notation' },
            { k: 'ndjson-gz',  label: 'NDJSON.gz',   icon: FileText, note: 'Full BSON fidelity (ObjectId / Date / Binary)' },
          ].map(f => (
            <label key={f.k} className={`flex gap-3 p-3 rounded-lg border cursor-pointer transition
              ${format === f.k ? 'border-fuchsia-500 bg-fuchsia-50/60' : 'border-neutral-200 hover:bg-neutral-50'}`}
              data-testid={`data-io-format-${f.k}`}>
              <input type="radio" checked={format === f.k} onChange={() => setFormat(f.k)}
                className="mt-1 accent-fuchsia-600" />
              <div>
                <div className="font-medium text-sm text-neutral-800 flex items-center gap-1.5"><f.icon className="w-4 h-4" />{f.label}</div>
                <div className="text-xs text-neutral-500 mt-0.5">{f.note}</div>
              </div>
            </label>
          ))}
        </div>
        {anyPiiSelected && (
          <label className="flex items-center gap-2 mt-4 p-3 rounded-lg bg-rose-50 border border-rose-200 cursor-pointer">
            <input type="checkbox" checked={includePii} onChange={e => setIncludePii(e.target.checked)}
              className="w-4 h-4 accent-rose-600" data-testid="data-io-include-pii" />
            <div className="text-sm">
              <div className="font-medium text-rose-700">Include PII in export</div>
              <div className="text-xs text-rose-600">When off, PII fields are replaced with hashed placeholders</div>
            </div>
          </label>
        )}
        <div className="mt-4 pt-4 border-t border-neutral-100 text-sm text-neutral-600">
          <div>Selected: <span className="font-semibold text-[#1a1a2e]">{selected.size}</span> collection(s)</div>
          <div>Est. size: <span className="font-semibold text-[#1a1a2e]">{estSize.toFixed(1)} KB</span></div>
        </div>
        <button onClick={download} disabled={downloading || !selected.size}
          className="mt-4 w-full px-4 py-2.5 bg-fuchsia-600 text-white rounded-lg font-medium hover:bg-fuchsia-700 disabled:opacity-50 flex items-center justify-center gap-2"
          data-testid="data-io-download-btn">
          {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          Download
        </button>
      </div>
    </div>
  );
}


// ─────────────────────────── IMPORT ───────────────────────────
function ImportTab() {
  const [step, setStep] = useState(1); // 1 upload · 2 review · 3 confirm · 4 done
  const [file, setFile] = useState(null);
  const [plan, setPlan] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [allowDeletes, setAllowDeletes] = useState(false);
  const [confirmPhrase, setConfirmPhrase] = useState('');
  const [result, setResult] = useState(null);
  const [expanded, setExpanded] = useState(new Set());

  const analyze = async () => {
    if (!file) return toast.error('Pick a file first');
    setAnalyzing(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await adminApi.post('/api/admin/data-io/import/analyze', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPlan(r.data);
      setStep(2);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Analyze failed');
    } finally { setAnalyzing(false); }
  };

  const apply = async () => {
    if (confirmPhrase !== 'APPLY') return toast.error('Type APPLY to confirm');
    setApplying(true);
    try {
      const r = await adminApi.post('/api/admin/data-io/import/apply', {
        plan_id: plan.plan_id,
        allow_deletes: allowDeletes,
        confirm_phrase: confirmPhrase,
      });
      setResult(r.data);
      setStep(4);
      toast.success('Changes applied');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Apply failed');
    } finally { setApplying(false); }
  };

  const rollback = async () => {
    if (!result?.backup_tag) return;
    if (!window.confirm('Roll back the entire import?')) return;
    try {
      await adminApi.post('/api/admin/data-io/rollback', { backup_tag: result.backup_tag });
      toast.success('Rolled back');
      reset();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Rollback failed');
    }
  };

  const reset = () => {
    setStep(1); setFile(null); setPlan(null); setResult(null);
    setConfirmPhrase(''); setAllowDeletes(false); setExpanded(new Set());
  };

  return (
    <div className="bg-white rounded-xl border border-neutral-200 p-6" data-testid="data-io-import">
      {/* Steps */}
      <ol className="flex items-center gap-3 text-xs text-neutral-500 mb-6">
        {['Upload', 'Review', 'Confirm', 'Done'].map((label, i) => (
          <li key={i} className={`flex items-center gap-2 ${step === i + 1 ? 'text-fuchsia-700 font-semibold' : step > i + 1 ? 'text-emerald-600' : ''}`}>
            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs
              ${step === i + 1 ? 'bg-fuchsia-600 text-white' : step > i + 1 ? 'bg-emerald-500 text-white' : 'bg-neutral-200'}`}>{i + 1}</span>
            {label}
            {i < 3 && <span>→</span>}
          </li>
        ))}
      </ol>

      {step === 1 && (
        <div>
          <label className="block border-2 border-dashed border-neutral-300 rounded-xl p-10 text-center cursor-pointer hover:border-fuchsia-500 hover:bg-fuchsia-50/30 transition"
            data-testid="data-io-dropzone">
            <Upload className="w-10 h-10 text-neutral-400 mx-auto mb-3" />
            <div className="text-sm font-medium text-neutral-700">
              {file ? file.name : 'Click to pick (.zip / .xlsx / .ndjson.gz)'}
            </div>
            <div className="text-xs text-neutral-400 mt-1">Max 50 MB</div>
            <input type="file" className="hidden"
              accept=".zip,.xlsx,.gz"
              onChange={e => setFile(e.target.files?.[0] || null)} />
          </label>
          <div className="flex justify-end mt-4">
            <button onClick={analyze} disabled={!file || analyzing}
              className="px-5 py-2 bg-fuchsia-600 text-white rounded-lg disabled:opacity-50 flex items-center gap-2"
              data-testid="data-io-analyze-btn">
              {analyzing && <Loader2 className="w-4 h-4 animate-spin" />}
              Analyze
            </button>
          </div>
        </div>
      )}

      {step === 2 && plan && (
        <div>
          {/* Totals */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            <StatPill tone="emerald" icon={Plus}   label="To add"       value={plan.totals.add} />
            <StatPill tone="amber"   icon={Pencil} label="To update"    value={plan.totals.update} />
            <StatPill tone="rose"    icon={Minus}  label="Missing (del?)" value={plan.totals.missing} />
            <StatPill tone="neutral" icon={CheckCircle2} label="Unchanged" value={plan.totals.unchanged} />
          </div>

          {/* Validation */}
          {(plan.validation.errors.length > 0 || plan.validation.warnings.length > 0) && (
            <div className="mb-5 space-y-2">
              {plan.validation.errors.map((e, i) => (
                <div key={i} className="flex items-start gap-2 text-sm p-3 bg-rose-50 border border-rose-200 rounded-lg">
                  <AlertOctagon className="w-4 h-4 text-rose-600 mt-0.5" />
                  <div><strong>{e.collection}</strong>: {e.issue}</div>
                </div>
              ))}
              {plan.validation.warnings.map((w, i) => (
                <div key={i} className="flex items-start gap-2 text-sm p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" />
                  <div><strong>{w.collection}</strong>: {w.issue}</div>
                </div>
              ))}
            </div>
          )}

          {/* Per-collection detail */}
          <div className="space-y-2">
            {Object.entries(plan.collections).map(([name, info]) => {
              const d = info.diff;
              const isOpen = expanded.has(name);
              const hasChange = d.add.count || d.update.count || d.missing.count || d.bad.count;
              return (
                <div key={name} className={`border rounded-lg ${hasChange ? 'border-neutral-300' : 'border-neutral-100 opacity-60'}`}
                  data-testid={`data-io-plan-${name}`}>
                  <button onClick={() => setExpanded(p => { const s = new Set(p); s.has(name) ? s.delete(name) : s.add(name); return s; })}
                    className="w-full flex items-center gap-3 p-3 text-left hover:bg-neutral-50">
                    {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    <span className="font-medium text-sm flex-1">{name}</span>
                    <ChangeBadges d={d} />
                  </button>
                  {isOpen && (
                    <div className="p-4 pt-0 text-xs text-neutral-600 space-y-3">
                      {d.add.count > 0 && (
                        <div>
                          <div className="font-semibold text-emerald-700 mb-1"><Plus className="w-3 h-3 inline" /> {d.add.count} to add</div>
                          <div className="text-neutral-500">IDs: {d.add.sample_ids.join(', ')}{d.add.count > 10 ? '…' : ''}</div>
                        </div>
                      )}
                      {d.update.count > 0 && (
                        <div>
                          <div className="font-semibold text-amber-700 mb-1"><Pencil className="w-3 h-3 inline" /> {d.update.count} to update (showing first {Math.min(10, d.update.count)})</div>
                          <div className="space-y-2">
                            {d.update.sample_diffs.map((u, i) => (
                              <div key={i} className="bg-neutral-50 rounded p-2">
                                <div className="font-mono text-[10px] text-neutral-500 mb-1">{u._id}</div>
                                {Object.entries(u.field_changes).map(([f, ch]) => (
                                  <div key={f} className="flex gap-2 flex-wrap">
                                    <span className="font-mono text-neutral-700">{f}:</span>
                                    <span className="line-through text-rose-700 bg-rose-50 px-1 rounded">{String(ch.old).slice(0, 80)}</span>
                                    <span>→</span>
                                    <span className="text-emerald-700 bg-emerald-50 px-1 rounded">{String(ch.new).slice(0, 80)}</span>
                                  </div>
                                ))}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {d.missing.count > 0 && (
                        <div>
                          <div className="font-semibold text-rose-700 mb-1"><Minus className="w-3 h-3 inline" /> {d.missing.count} rows exist in DB but not in file</div>
                <div className="text-xs text-rose-600">Will only be deleted if &ldquo;Allow deletes&rdquo; is ticked at confirm.</div>
                          <div className="text-neutral-500 mt-1">IDs: {d.missing.sample_ids.join(', ')}{d.missing.count > 10 ? '…' : ''}</div>
                        </div>
                      )}
                      {d.bad.count > 0 && (
                        <div className="text-rose-700">⚠ {d.bad.count} malformed row(s) in file — fix and re-analyze</div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="flex justify-between mt-5">
            <button onClick={reset} className="px-5 py-2 border border-neutral-300 rounded-lg">Cancel</button>
            <button onClick={() => setStep(3)}
              disabled={plan.validation.errors.length > 0}
              className="px-5 py-2 bg-fuchsia-600 text-white rounded-lg disabled:opacity-50"
              data-testid="data-io-continue-confirm">
              Continue → Confirm
            </button>
          </div>
        </div>
      )}

      {step === 3 && plan && (
        <div className="max-w-xl">
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg mb-4">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div className="text-sm text-amber-900">
                <strong>Before you apply:</strong> This writes to the live database. A backup of every affected collection
                will be taken automatically. You can roll back from the Backups tab for 30 days.
              </div>
            </div>
          </div>
          {plan.totals.missing > 0 && (
            <label className="flex items-center gap-3 p-3 border border-rose-200 bg-rose-50 rounded-lg mb-4 cursor-pointer">
              <input type="checkbox" checked={allowDeletes} onChange={e => setAllowDeletes(e.target.checked)}
                className="w-4 h-4 accent-rose-600" data-testid="data-io-allow-deletes" />
              <div className="text-sm">
                <div className="font-semibold text-rose-700">Allow deletes</div>
                <div className="text-xs text-rose-600">{plan.totals.missing} row(s) exist in DB but not in the file. Tick to delete them.</div>
              </div>
            </label>
          )}
          <label className="block mb-1 text-sm font-medium text-neutral-700">Type <code className="px-1 bg-neutral-100 rounded">APPLY</code> to confirm</label>
          <input type="text" value={confirmPhrase} onChange={e => setConfirmPhrase(e.target.value)}
            placeholder="APPLY"
            className="w-full px-4 py-2 border border-neutral-300 rounded-lg text-sm"
            data-testid="data-io-confirm-input" />
          <div className="flex justify-between mt-5">
            <button onClick={() => setStep(2)} className="px-5 py-2 border border-neutral-300 rounded-lg">Back</button>
            <button onClick={apply} disabled={confirmPhrase !== 'APPLY' || applying}
              className="px-5 py-2 bg-rose-600 text-white rounded-lg disabled:opacity-50 flex items-center gap-2"
              data-testid="data-io-apply-btn">
              {applying && <Loader2 className="w-4 h-4 animate-spin" />}
              Apply changes
            </button>
          </div>
        </div>
      )}

      {step === 4 && result && (
        <div>
          <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg mb-4 flex items-start gap-2">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
            <div className="text-sm text-emerald-900">
              <div><strong>Changes applied.</strong> Backup tag: <code>{result.backup_tag}</code></div>
            </div>
          </div>
          <div className="space-y-2 mb-5">
            {Object.entries(result.applied).map(([name, s]) => (
              <div key={name} className="flex items-center gap-3 p-3 border border-neutral-200 rounded-lg">
                <span className="font-medium flex-1">{name}</span>
                <span className="text-xs text-emerald-700">+{s.added}</span>
                <span className="text-xs text-amber-700">↻{s.updated}</span>
                <span className="text-xs text-rose-700">−{s.deleted}</span>
              </div>
            ))}
          </div>
          <div className="flex gap-3">
            <button onClick={rollback}
              className="px-5 py-2 bg-rose-600 text-white rounded-lg flex items-center gap-2"
              data-testid="data-io-rollback-btn">
              <RotateCcw className="w-4 h-4" />Roll back this import
            </button>
            <button onClick={reset}
              className="px-5 py-2 border border-neutral-300 rounded-lg">
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function StatPill({ tone, icon: Icon, label, value }) {
  const tones = {
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    amber:   'bg-amber-50 text-amber-700 border-amber-200',
    rose:    'bg-rose-50 text-rose-700 border-rose-200',
    neutral: 'bg-neutral-50 text-neutral-700 border-neutral-200',
  };
  return (
    <div className={`p-3 rounded-lg border ${tones[tone]}`}>
      <div className="text-xs opacity-80 flex items-center gap-1"><Icon className="w-3 h-3" />{label}</div>
      <div className="text-xl font-bold">{value}</div>
    </div>
  );
}

function ChangeBadges({ d }) {
  return (
    <div className="flex items-center gap-1 text-[11px]">
      {d.add.count > 0 &&     <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded border border-emerald-200">+{d.add.count}</span>}
      {d.update.count > 0 &&  <span className="px-2 py-0.5 bg-amber-50 text-amber-700 rounded border border-amber-200">↻{d.update.count}</span>}
      {d.missing.count > 0 && <span className="px-2 py-0.5 bg-rose-50 text-rose-700 rounded border border-rose-200">−{d.missing.count}</span>}
      {d.unchanged.count > 0 && d.add.count + d.update.count + d.missing.count === 0 &&
        <span className="px-2 py-0.5 bg-neutral-50 text-neutral-600 rounded border border-neutral-200">= {d.unchanged.count}</span>}
      {d.bad.count > 0 && <span className="px-2 py-0.5 bg-rose-100 text-rose-800 rounded border border-rose-300">!{d.bad.count}</span>}
    </div>
  );
}

// ─────────────────────────── BACKUPS ───────────────────────────
function BackupsTab() {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try { setLoading(true);
      const r = await adminApi.get('/api/admin/data-io/backups');
      setBackups(r.data.backups || []);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Load failed'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const rollback = async (tag) => {
    if (!window.confirm(`Roll the database back to the state captured at ${tag}?`)) return;
    setBusy(tag);
    try {
      const r = await adminApi.post('/api/admin/data-io/rollback', { backup_tag: tag });
      toast.success(`Rolled back ${r.data.count} collection(s)`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Rollback failed'); }
    finally { setBusy(null); }
  };
  const purge = async (tag) => {
    if (!window.confirm(`Permanently delete backup ${tag}? This cannot be undone.`)) return;
    setBusy(tag);
    try {
      await adminApi.delete(`/api/admin/data-io/backups/${encodeURIComponent(tag)}`);
      toast.success('Backup deleted');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
    finally { setBusy(null); }
  };

  if (loading) return <div className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin inline" /></div>;
  if (!backups.length) return <div className="p-8 text-center text-neutral-400">No backups yet. Backups are created automatically on every Apply.</div>;

  return (
    <div className="space-y-3">
      <div className="text-xs text-neutral-500">Backups older than 30 days are auto-pruned.</div>
      {backups.map(b => (
        <div key={b.tag} className="bg-white border border-neutral-200 rounded-xl p-4" data-testid={`backup-${b.tag}`}>
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <div className="font-semibold text-[#1a1a2e]">{b.tag}</div>
              <div className="text-xs text-neutral-500">
                {b.created_at ? new Date(b.created_at).toLocaleString() : '?'} · {b.age_days ?? '?'} days old · {b.collections.length} collection(s) · {b.total_count} rows · {b.total_size_kb.toFixed(1)} KB
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => rollback(b.tag)} disabled={busy === b.tag}
                className="px-3 py-1.5 bg-fuchsia-600 text-white rounded-lg text-sm flex items-center gap-1 disabled:opacity-50"
                data-testid={`rollback-${b.tag}`}>
                <RotateCcw className="w-3.5 h-3.5" />Rollback
              </button>
              <button onClick={() => purge(b.tag)} disabled={busy === b.tag}
                className="px-3 py-1.5 bg-neutral-100 text-neutral-700 rounded-lg text-sm flex items-center gap-1 disabled:opacity-50"
                data-testid={`purge-${b.tag}`}>
                <Trash2 className="w-3.5 h-3.5" />Delete
              </button>
            </div>
          </div>
          <div className="mt-3 text-xs text-neutral-600 flex flex-wrap gap-2">
            {b.collections.map(c => (
              <span key={c.original} className="px-2 py-0.5 bg-neutral-50 rounded border border-neutral-200">
                {c.original} · {c.count}
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────── AUDIT ───────────────────────────
function AuditTab() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const r = await adminApi.get('/api/admin/audit-logs?target_type=data_io&limit=50');
        setLogs(r.data.logs || r.data.audit_logs || r.data || []);
      } catch (e) {
        // Soft fail — not critical
      } finally { setLoading(false); }
    })();
  }, []);
  if (loading) return <div className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin inline" /></div>;
  if (!logs.length) return <div className="p-8 text-center text-neutral-400">No Data-IO audit entries yet.</div>;
  return (
    <div className="space-y-2">
      {logs.map((l, i) => (
        <div key={i} className="bg-white border border-neutral-200 rounded-lg p-3 text-sm">
          <div className="flex items-center gap-2">
            <span className="font-semibold">{l.action}</span>
            <span className="text-xs text-neutral-500">{l.actor_email}</span>
            <span className="text-xs text-neutral-400 ml-auto">{l.timestamp ? new Date(l.timestamp).toLocaleString() : ''}</span>
          </div>
          {l.payload && (
            <pre className="mt-2 text-[10px] bg-neutral-50 p-2 rounded overflow-x-auto">{JSON.stringify(l.payload, null, 2)}</pre>
          )}
        </div>
      ))}
    </div>
  );
}
